import type { InputAction } from "./EventBus";

/** A press that starts on one of these keeps its own behaviour (no swipe, no tap) — buttons stay tappable. */
const CONTROL = "button, input, select, textarea, a, label, [role=button], [data-action], [contenteditable]";
/** Swipe threshold in CSS px at sensitivity 1 (the setting divides it: 0.5 → 52 px, 2 → 13 px). */
const SWIPE_PX = 26;
/** A press longer than this (or one that moved past the threshold) is not a tap. */
const TAP_MAX_MS = 350;
/** Two taps this close together (release to release) = hover board. */
const DOUBLE_TAP_MS = 300;
/** A click whose press began off any control and lands on one within this window is a ghost click. */
const GHOST_MS = 1000;

/** Physical keys (`event.code`: WASD stays WASD on AZERTY/Dvorak); `event.key` below catches the printed letters too. */
const CODES: Record<string, InputAction | undefined> = {
  ArrowLeft: "left", KeyA: "left",
  ArrowRight: "right", KeyD: "right",
  ArrowUp: "jump", KeyW: "jump", Space: "jump",
  ArrowDown: "roll", KeyS: "roll",
  ShiftLeft: "board", ShiftRight: "board",
  Escape: "pause", KeyP: "pause"
};
const KEYS: Record<string, InputAction | undefined> = {
  ArrowLeft: "left", a: "left", A: "left",
  ArrowRight: "right", d: "right", D: "right",
  ArrowUp: "jump", w: "jump", W: "jump", " ": "jump",
  ArrowDown: "roll", s: "roll", S: "roll",
  Shift: "board", Escape: "pause", p: "pause", P: "pause"
};

type Gesture = { x: number; y: number; t: number; fired: boolean; mouse: boolean };

/**
 * Keys on desktop, swipes on touch (and mouse drags). One swipe per gesture, measured from the press, fired the moment it
 * passes the threshold (mid-gesture, like the reference runner), not on release; every finger is its own gesture.
 * A double tap = hover board. Also owns the page-level guards: no ghost clicks, no pinch zoom (iOS), no callouts.
 */
export class InputManager {
  private gestures = new Map<number, Gesture>();
  private lastTapAt = -1e9;
  private sensitivity = 1;
  /** where the latest press began: on a control (a click may follow) or on the game (a click on a control is a ghost) */
  private pressOnControl = true;
  private pressAt = -1e9;
  private readonly abort = new AbortController();

  constructor(private target: HTMLElement, private dispatch: (action: InputAction) => void) {
    const signal = this.abort.signal;
    window.addEventListener("keydown", this.onKey, { signal, passive: false });
    target.addEventListener("pointerdown", this.onPointerDown, { signal });
    target.addEventListener("pointermove", this.onPointerMove, { signal });
    target.addEventListener("pointerup", this.onPointerUp, { signal });
    target.addEventListener("pointercancel", this.onPointerCancel, { signal });
    target.addEventListener("contextmenu", this.onContextMenu, { signal });
    window.addEventListener("pointerdown", this.notePress, { signal, capture: true });
    window.addEventListener("click", this.onClick, { signal, capture: true });
    window.addEventListener("blur", this.reset, { signal });
    // iOS Safari ignores user-scalable=no; its own gesture events are the way to stop a pinch zoom
    for (const type of ["gesturestart", "gesturechange", "gestureend"]) document.addEventListener(type, this.prevent, { signal, passive: false });
  }

  setSensitivity(value: number): void {
    this.sensitivity = Number.isFinite(value) && value > 0 ? value : 1;
  }

  /** Forget gestures in flight and the last tap (a screen change: the tap that started a run is not half a double tap). */
  reset = (): void => {
    this.gestures.clear();
    this.lastTapAt = -1e9;
  };

  destroy(): void {
    this.abort.abort();
    this.reset();
  }

  private get threshold(): number {
    return Math.min(64, Math.max(10, SWIPE_PX / this.sensitivity));
  }

  private onKey = (event: KeyboardEvent): void => {
    const el = event.target as HTMLElement | null;
    if (event.isComposing || el?.closest?.("input, textarea, select, [contenteditable]")) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;                   // browser shortcuts stay browser shortcuts
    const action = CODES[event.code] ?? KEYS[event.key];
    if (!action) return;
    // Space / Enter on a focused button press that button (menus by keyboard); the run blurs focus, so there it is a hop
    if ((event.code === "Space" || event.key === " ") && el?.closest?.("button, a, [role=button]")) return;
    event.preventDefault();                                                         // arrows and space never scroll the page
    if (event.repeat) return;                                                       // a held key is one swipe, not a stream of them
    this.dispatch(action);
  };

  private onPointerDown = (event: PointerEvent): void => {
    if ((event.target as Element | null)?.closest?.(CONTROL)) return;
    const mouse = event.pointerType === "mouse";
    if (mouse && event.button !== 0) return;
    this.gestures.set(event.pointerId, { x: event.clientX, y: event.clientY, t: performance.now(), fired: false, mouse });
    // a mouse drag that leaves the window still ends here (touch has implicit capture)
    if (mouse) { try { this.target.setPointerCapture(event.pointerId); } catch { /* not capturable */ } }
  };

  private onPointerMove = (event: PointerEvent): void => {
    const g = this.gestures.get(event.pointerId);
    if (!g || g.fired) return;
    if (g.mouse && event.buttons === 0) { this.gestures.delete(event.pointerId); return; }   // released where we could not see it
    this.trySwipe(g, event.clientX - g.x, event.clientY - g.y);
  };

  private onPointerUp = (event: PointerEvent): void => {
    const g = this.gestures.get(event.pointerId);
    if (!g) return;
    this.gestures.delete(event.pointerId);
    if (g.fired) return;
    if (this.trySwipe(g, event.clientX - g.x, event.clientY - g.y)) return;      // a flick whose moves were coalesced away
    const now = performance.now();
    if (now - g.t > TAP_MAX_MS) return;                                            // a long press is not a tap
    if (now - this.lastTapAt <= DOUBLE_TAP_MS) {
      this.lastTapAt = -1e9;
      this.dispatch("board");
    } else {
      this.lastTapAt = now;
    }
  };

  private onPointerCancel = (event: PointerEvent): void => {
    this.gestures.delete(event.pointerId);
  };

  private trySwipe(g: Gesture, dx: number, dy: number): boolean {
    const limit = this.threshold;
    if (Math.abs(dx) < limit && Math.abs(dy) < limit) return false;
    g.fired = true;
    this.lastTapAt = -1e9;
    this.dispatch(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? "right" : "left") : (dy > 0 ? "roll" : "jump"));
    return true;
  }

  /** Every press, before anyone else sees it: did it start on a control? */
  private notePress = (event: PointerEvent): void => {
    this.pressOnControl = !!(event.target as Element | null)?.closest?.(CONTROL);
    this.pressAt = performance.now();
  };

  /**
   * Ghost clicks: a tap on the game (start the run, close an overlay) re-renders the screen, and the click that follows
   * the release can land on a button that was not there at the press. A click on a control whose press began off every
   * control is dropped. Keyboard and programmatic clicks (detail 0) always pass.
   */
  private onClick = (event: MouseEvent): void => {
    if (event.detail === 0 || this.pressOnControl) return;
    if (performance.now() - this.pressAt > GHOST_MS) return;
    if (!(event.target as Element | null)?.closest?.(CONTROL)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /** No long-press menu / right-click menu over the game; text fields and links keep theirs. */
  private onContextMenu = (event: Event): void => {
    if ((event.target as Element | null)?.closest?.("input, textarea, a")) return;
    event.preventDefault();
  };

  private prevent = (event: Event): void => {
    event.preventDefault();
  };
}
